import { PageBreadcrumb } from "@/layouts/page-container/shared/breadcrumb";
import { Link, Outlet, useLocation } from "react-router-dom";

import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { Separator } from "@/components/ui/separator";

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/features/authentication";
import { Filters } from "./components/filters/filters";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchPrivateOffices } from "@/hooks/useSearchPrivateOffices";
import { privateOfficeService } from "@/services/private-office-service";
import {
  Filters as PainelFilters,
  PainelFiltersList,
} from "./components/painel-filters";
import { DataTableContainer } from "./components/data-table-container";
import { CsvUploadModal } from "../modal-csv-upload";
import { STATUS_PRIVATE_OFFICE } from "@/constants/status-private-office";
import { createToast } from "vercel-toast";
import { Button } from "@/components/ui/button";
import { usePrivateOfficeCreateRoute } from "@/constants/routes";
import { PlusIcon } from "lucide-react";
import { SearchPrivateRoomEventType } from "@/features/analytics";
import { debounce } from "lodash";
import { SPACE_TYPE } from "@/constants/space-type";
import { OrderAlert } from "@/components/order-alert";

const SPACE_ID_NOT_SHOW = 1539;
const ACTIVE_SKIP_RECENT_UPDATES_FILTER = true;

type FormData = {
  uf?: string;
  filters: PainelFilters;
  deal_url?: string | null;
  searchType: "location" | "space" | "privateOfficeIds" | "neighborhoods";
  spaceId?: number;
  privateOfficeIds?: string[] | null;
  cityName?: string;
  neighborhood?: string[];
};

export const PrivateRoomAdmin = () => {
  const {
    privateOffices,
    handleSearchPrivateOffices,
    isLoading: isLoadingPrivateOffices,
    setIsLoading: setIsLoadingPrivateOffices,
    setPrivateOffices,
  } = useSearchPrivateOffices();
  const createRoute = usePrivateOfficeCreateRoute();

  const [searchSelectedValue, setSearchSelectedValue] = useState("");
  const { userInfo } = useAuth();
  const { pathname } = useLocation();
  const [hasSearched, setHasSearched] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [isOpenFilters, setIsOpenFilters] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      filters: {
        minCapacity: undefined,
        maxCapacity: undefined,
        unitCreditPrice: 80,
        minPrice: undefined,
        maxPrice: undefined,
        amenitiesSlugs: [],
        privateOfficeStatus: [STATUS_PRIVATE_OFFICE.AVAILABLE.toString()],
        distance: 30,
        skipRecentUpdatesFilter: ACTIVE_SKIP_RECENT_UPDATES_FILTER,
      },
      searchType: "privateOfficeIds",
      spaceId: undefined,
      privateOfficeIds: undefined,
      cityName: undefined,
      neighborhood: undefined,
    },
  });

  const debounceSubmit = useRef(
    debounce((searchValue?: string) => {
      handleFormSubmit("SEARCH_SUBMITTED", searchValue);
    }, 1000),
  );

  useEffect(() => {
    return () => {
      debounceSubmit.current.cancel();
    };
  }, []);

  const handleFormSubmit = (
    eventName: SearchPrivateRoomEventType,
    searchValue?: string,
  ) => {
    form.handleSubmit(async (data) => {
      const { deal_url: _deal_url, ...demandData } = data;

      const uf =
        searchValue && searchValue.match(/- ([A-Z]{2}),/)
          ? searchValue?.match(/- ([A-Z]{2}),/)
          : null;

      if (
        !data.searchType ||
        (data.searchType === "location" && !searchValue) ||
        (data.searchType === "space" && !data?.spaceId) ||
        (data.searchType === "privateOfficeIds" &&
          (!data?.privateOfficeIds || data?.privateOfficeIds.length === 0)) ||
        (data.searchType === "neighborhoods" &&
          (!data?.neighborhood || data?.neighborhood.length === 0))
      ) {
        createToast(
          "É necessário informar uma localização, sala, espaço ou bairro para continuar!",
          {
            type: "error",
            timeout: 5000,
          },
        );
        return;
      }

      const isSearchByIds = data.searchType === "privateOfficeIds";

      const demandsPayload = isSearchByIds
        ? {
            filters: {},
            searchType: data.searchType,
            privateOfficeIds: data.privateOfficeIds,
            unitCreditPrice:
              Number(data.filters.unitCreditPrice) === 0
                ? null
                : Number(data.filters.unitCreditPrice),
            skipRecentUpdatesFilter:
              data.filters.skipRecentUpdatesFilter ??
              ACTIVE_SKIP_RECENT_UPDATES_FILTER,
            lifecycleStates: data.filters.lifecycleStates ?? [],
            includeLegacy: true,
            includeDeleted: false,
          }
        : {
            ...demandData,
            distance: Number(data.filters.distance),
            minCapacity:
              Number(data.filters.minCapacity) === 0
                ? null
                : Number(data.filters.minCapacity),
            maxCapacity:
              Number(data.filters.maxCapacity) === 0
                ? null
                : Number(data.filters.maxCapacity),
            minPrice:
              Number(data.filters.minPrice) === 0
                ? null
                : Number(data.filters.minPrice),
            maxPrice:
              Number(data.filters.maxPrice) === 0
                ? null
                : Number(data.filters.maxPrice),
            unitCreditPrice:
              Number(data.filters.unitCreditPrice) === 0
                ? null
                : Number(data.filters.unitCreditPrice),
            amenitiesSlugs: data.filters.amenitiesSlugs,
            privateOfficeStatus:
              data.filters.privateOfficeStatus?.map(Number) ?? [],
            lifecycleStates: data.filters.lifecycleStates ?? [],
            uf: uf?.[1] ?? undefined,
            skipRecentUpdatesFilter:
              data.filters.skipRecentUpdatesFilter ??
              ACTIVE_SKIP_RECENT_UPDATES_FILTER,
            includeLegacy: true,
            includeDeleted: false,
          };

      await handleSearchPrivateOffices({
        term: searchValue,
        demands: {
          [searchValue || data.searchType]: demandsPayload,
        },
        dealUrl: data.deal_url || undefined,
        spaceTypes: [SPACE_TYPE.COWORKING, SPACE_TYPE.FLOOR_BTS],
      });

      setHasSearched(true);

      sendTrackDataServer({
        eventName: eventName,
        data: {
          user_id: userInfo.user?.id ?? null,
          email: userInfo.profile?.email ?? null,
          entry_point: "search_input",
          filters: {
            term: searchValue,
            ...data,
          },
          deal_url: data.deal_url || null,
        },
        url: location.pathname,
        productType: "PO",
      });
    })();
  };

  const handleSearchSubmitAuto = useCallback(
    (locationSearchValue?: string) => {
      debounceSubmit.current(locationSearchValue);
    },
    [debounceSubmit],
  );

  const handleSearchSubmit = useCallback(() => {
    handleFormSubmit("SEARCH_BUTTON_CLICKED", searchSelectedValue);
  }, [searchSelectedValue]);

  const handleCsvUpload = async (file: File) => {
    if (!file) return;
    try {
      setIsLoadingPrivateOffices(true);
      const response = await privateOfficeService.uploadCsv(file);

      if (response && typeof response === "object") {
        setPrivateOffices(response);
        setHasSearched(true);
        setShowCsvModal(false);
      } else {
        console.error("Resposta inválida do upload CSV:", response);
        alert("Formato de resposta inválido. Tente novamente.");
      }
    } catch (error: any) {
      alert("Erro ao enviar o arquivo. Tente novamente.");
      console.error("Erro no upload do CSV:", error);
    } finally {
      setIsLoadingPrivateOffices(false);
    }
  };

  const parseData = useMemo(() => {
    if (privateOffices?.results) {
      const filteredData = privateOffices.results.filter(
        (item) => item.space_id !== SPACE_ID_NOT_SHOW,
      );
      return filteredData;
    }
    return [];
  }, [privateOffices]);

  useEffect(() => {
    if (hasSearched && parseData) {
      sendTrackDataServer({
        eventName: "SEARCH_RESULTS_RENDERED",
        data: {
          user_id: userInfo.user?.id?.toString(),
          email: userInfo.profile?.email,
          deal_url: form.getValues("deal_url")?.toString() ?? null,
          has_results: parseData.length > 0,
          results_count: parseData.length,
          filters: {
            term: searchSelectedValue,
            ...form.getValues(),
          },
          type: "multiple",
        },
        url: location.pathname,
        productType: "PO",
      });
    }
  }, [parseData, hasSearched, userInfo, searchSelectedValue, pathname, form]);

  return (
    <FormProvider {...form}>
      <div>
        <div className="flex flex-col w-full h-[calc(100vh-80px)] overflow-hidden">
          <PageBreadcrumb title="Área administrativa">
            <Button className="ml-auto">
              <Link
                to={createRoute}
                onClick={() => {
                  sendTrackDataServer({
                    eventName: "CREATE_NEW_ROOM_CLICKED",
                    data: { entry_point: "list_button" },
                    url: location.pathname,
                    productType: "PO",
                  });
                }}
                className="flex "
                target="_blank"
              >
                <PlusIcon className="w-5" />
                Nova Sala Privativa
              </Link>
            </Button>
          </PageBreadcrumb>
          <OrderAlert variant="list" className="mx-6 my-6" />
          <div className="flex flex-col px-6 gap-1">
            <Separator className="my-2" />

            <div className="flex items-start gap-2">
              <Filters
                setSearchSelectedValue={setSearchSelectedValue}
                handleSearchSubmit={handleSearchSubmit}
                handleSearchSubmitAuto={handleSearchSubmitAuto}
                onFilterClick={() => setIsOpenFilters(true)}
              />
            </div>
          </div>

          <div className="flex w-full h-full p-6 pt-4">
            <DataTableContainer
              parseData={parseData}
              isLoadingPrivateOffices={isLoadingPrivateOffices}
              hasSearched={hasSearched}
            />
          </div>
        </div>
      </div>
      <Outlet />
      <CsvUploadModal
        open={showCsvModal}
        onClose={() => setShowCsvModal(false)}
        onUpload={handleCsvUpload}
        setError={form.setError}
        clearErrors={form.clearErrors}
        errors={form.formState.errors}
      />
      <PainelFiltersList
        isOpen={isOpenFilters}
        onClose={() => setIsOpenFilters(false)}
        onSearchSubmit={handleSearchSubmit}
      />
    </FormProvider>
  );
};
